import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import KeyboardArrowDownRounded from '@mui/icons-material/KeyboardArrowDownRounded';
import type { SelectChangeEvent } from '@mui/material';
import { MenuItem, Select, Typography } from '@mui/material';

import { getLanguageAbbreviation } from 'src/helper/helper';
import { useDashboardContext } from 'src/components/dashboard/DashboardContext';

import { styles } from './index.styles';

export const LANGUAGE_SELECT_TEST_ID = 'language-select';

const menuProps = {
  PaperProps: {
    sx: styles.menuProps,
  },
};

interface LanguageSelectProps {
  selectedLanguage: number;
  setSelectedLanguage: (language: number) => void;
}

const LanguageSelect: FC<LanguageSelectProps> = ({
  selectedLanguage,
  setSelectedLanguage,
}) => {
  const { t } = useTranslation('navigation');
  const { dashboardConfig } = useDashboardContext();

  const handleChange = (event: SelectChangeEvent) => {
    const newLanguage = dashboardConfig?.supported_languages.find(
      (lang) => lang.abbreviation === event.target.value,
    );
    if (newLanguage) {
      setSelectedLanguage(newLanguage.code);
    }
  };

  return (
    <Select
      data-test-id={LANGUAGE_SELECT_TEST_ID}
      value={
        dashboardConfig?.supported_languages
          ? getLanguageAbbreviation(
              selectedLanguage,
              dashboardConfig?.supported_languages,
            ) || dashboardConfig?.supported_languages[0]?.abbreviation
          : ''
      }
      onChange={handleChange}
      sx={styles.select}
      IconComponent={KeyboardArrowDownRounded}
      variant="outlined"
      MenuProps={menuProps}
    >
      {dashboardConfig?.supported_languages.map((item) => (
        <MenuItem
          key={item.abbreviation}
          value={item.abbreviation}
          sx={styles.menuItem}
        >
          <Typography variant="button2">
            {t(`language.${item.abbreviation.toLowerCase()}`)}
          </Typography>
        </MenuItem>
      ))}
    </Select>
  );
};

export default LanguageSelect;
